import {ResponseInnerMessage} from '@/hosted_fields/common/types';
import IframeClient from '@/hosted_fields/common/connection/client';
import '@/helpers/polyfills';
import ThreeDSPluginInterface from '@/plugins/three_domain_secure/actions/interface';
import {ApiClientInterface} from '@/plugins/core/api/interface';
import {PluginConfig, PluginName} from '@/plugins/core/interface';
import ThreeDSApis from '@/plugins/three_domain_secure/api';
import CommManagerInterface from '@/hosted_fields/master/interface';
import {CbError} from '@/hosted_fields/common/errors';
import Logger from '@/utils/logger_old';
import {
  PaymentAttempt,
  PaymentAttemptStatus,
  PaymentIntent,
  ConfirmApiPayload,
  PaymentMethodType,
  CancelPaymentIntentRequestPayload,
} from '@/extensions/three_domain_secure/common/types';
import ComponentsAndFieldsPluginInterface from '@/plugins/components_fields/actions/interface';
import {Field} from '@/hosted_fields/common/enums';
import {getPaymentIntentApiHeaders} from '@/internal/common/utils';
import {ThreeDSPollingInterval} from '@/constants/enums';

export default class ThreeDSActions implements ThreeDSPluginInterface {
  config: PluginConfig;
  private iframeClient: IframeClient;
  private loadConfig: Promise<any>;
  private configuration: any;
  private apiClient: ApiClientInterface;
  private store: any;
  private commMgr: CommManagerInterface;

  // Payment intent status poll Promise handlers
  private piResolver: Function;
  private piRejector: Function;

  init(): Promise<void> {
    this.apiClient.addApis(ThreeDSApis);
    if (this.apiClient._config.publishableKey) {
      this.loadConfig = this.apiClient.config
        .retrieve()
        .then((config) => {
          this.configuration = config;
        })
        .catch((error) => {
          const err = new CbError(error);
          Logger.error(err);
          throw err;
        });
    } else {
      this.loadConfig = Promise.resolve(true);
    }
    return Promise.resolve();
  }

  retrievePaymentIntent(data: any): Promise<ResponseInnerMessage> {
    return this.apiClient.payment_intent.retrieve({}, {}, {headers: {Authorization: `Bearer ${data.paymentIntentId}`}});
  }

  setupPaymentIntentWorker(data: ConfirmApiPayload): Promise<ResponseInnerMessage> {
    const paymentIntentId = data.paymentIntentId;
    const businessEntityId = data.businessEntityId;
    let _this = this;

    // Initialize payment intent worker
    this.commMgr.piWorker.postMessage({
      action: 'initializeWorker',
      data: {
        site: this.commMgr.site,
        paymentIntentId,
        businessEntityId,
        options: {
          hostName: this.commMgr.hostName,
          customDomain: this.commMgr.site_meta.options.domain,
        },
      },
    });

    // Payment Intent worker - Response handler
    this.commMgr.piWorker.onmessage = (e) => {
      /**
       * CHKOUTENGG-7431
       * Safari considers 'event' context as 'this'
       * So use a closure variable to access the parent instance
       */
      const payload = e.data;
      const paymentIntent: PaymentIntent = payload.data.payment_intent;
      const attempt: PaymentAttempt = paymentIntent.active_payment_attempt;
      switch (payload.action) {
        case 'paymentIntentResult': {
          Logger.kvl({
            hp_js_action: 'paymentIntentResult',
            hp_payment_intent_id: paymentIntent.id,
            hp_payment_intent_attempt_status: attempt.status,
            hp_payment_intent_gw_name: paymentIntent.gateway,
            hp_payment_intent_gw_account_id: paymentIntent.gateway_account_id,
          });
          if (attempt.status == PaymentAttemptStatus.AUTHORIZED) {
            _this.piResolver && _this.piResolver(payload.data);
          } else if (attempt.status == PaymentAttemptStatus.REFUSED) {
            _this.piRejector && _this.piRejector(payload.data);
          }
          break;
        }
        case 'paymentIntentPollTimeout': {
          _this.piRejector && _this.piRejector({message: 'Poll Timeout'});
          break;
        }
      }
    };

    // Will delay polling 2 second, since loading 3ds page will take some time and currently we starts polling along with confirm call
    // to fix GATEENGG-20574, razorpay issue.
    setTimeout(() => {
      // Start payment intent status poll
      this.commMgr.piWorker.postMessage({
        action: 'startPaymentIntentPoll',
      });
    }, 2000);

    return Promise.resolve({acknowledged: true});
  }

  getDetailsFromCardComponent(componentName: string): Promise<any> {
    if (!this.commMgr.pluginMan.isPluginLoaded(PluginName.COMPONENTS_FIELDS)) {
      const err = new CbError('Components & Fields plugin not loaded');
      return Promise.reject(err);
    } else {
      return this.commMgr.pluginMan
        .getPlugin(PluginName.COMPONENTS_FIELDS)
        .then((cardComponent: ComponentsAndFieldsPluginInterface) => {
          return cardComponent
            .getCardDetails(componentName)
            .then((cardDetails) => cardComponent.getCardValuesFromDetails(cardDetails));
        })
        .then((card) => {
          let [expiryMonth, expiryYear] = card[Field.Expiry].split('/');

          // Hard coding year to "20"
          const yearPrefix = new Date().getFullYear().toString().slice(0, 2);
          const fullYear = `${yearPrefix}${expiryYear}`;

          return {
            number: `${card[Field.Number]}`.replace(/\s/g, ''),
            expiryMonth: expiryMonth,
            expiryYear: fullYear,
            cvv: card[Field.CVV],
          };
        });
    }
  }

  confirmPaymentIntent(data: ConfirmApiPayload): Promise<ResponseInnerMessage> {
    const paymentIntent = data.payload && data.payload.paymentIntent;
    if (data.payload && data.payload.paymentIntent) delete data.payload.paymentIntent;

    let _promise: Promise<any> = Promise.resolve(data.payload);

    const referrerModule =
      window &&
      window['cb_site_info'] &&
      window['cb_site_info']['site_meta'] &&
      window['cb_site_info']['site_meta']['options']
        ? window['cb_site_info']['site_meta']['options']['referrerModule']
        : undefined;

    if (data.payload) {
      const isHpV3Module = ['gift_v3', 'checkout_v3', 'paynow_v3', 'portal_v3', 'view_voucher_v3'].includes(
        referrerModule
      );
      data.payload.requestSource = isHpV3Module ? 'cb_hp' : referrerModule || 'cb_js';
    }

    // if components & fields
    if (data.payload && data.payload.cardComponent) {
      _promise = this.getDetailsFromCardComponent(data.payload.cardComponent).then((cardDetails) => {
        const payload = data.payload;
        payload.paymentMethod = cardDetails;
        payload.paymentMethodType = PaymentMethodType.CARD;
        // GATEENGG-14935 - Unable to pass owner name for Adyen
        if (
          payload.paymentMethod &&
          !payload.paymentMethod.firstName &&
          !payload.paymentMethod.lastName &&
          payload.billingAddress &&
          (payload.billingAddress.firstName || payload.billingAddress.lastName)
        ) {
          payload.paymentMethod.firstName = payload.billingAddress.firstName;
          payload.paymentMethod.lastName = payload.billingAddress.lastName;
        }
        delete payload.cbToken;
        delete payload.cardComponent;
        return payload;
      });
    } else if (data.payload && data.payload.paymentMethodDetails && data.payload.paymentMethodDetails.cardComponent) {
      _promise = this.getDetailsFromCardComponent(data.payload.paymentMethodDetails.cardComponent).then(
        (cardDetails) => {
          const payload = data.payload;
          payload.paymentMethodDetails.card = cardDetails;
          payload.paymentMethodType = PaymentMethodType.CARD;
          if (
            payload.paymentMethodDetails &&
            !payload.paymentMethodDetails.firstName &&
            !payload.paymentMethodDetails.lastName &&
            payload.customer &&
            (payload.customer.firstName || payload.customer.lastName)
          ) {
            payload.paymentMethod.firstName = payload.customer.firstName;
            payload.paymentMethod.lastName = payload.customer.lastName;
          }
          delete payload.paymentMethodDetails.cardComponent;
          return payload;
        }
      );
    }

    return _promise
      .then((payload) =>
        this.apiClient.payment_intent.confirm({paymentIntentId: data.paymentIntentId}, payload, {
          headers: getPaymentIntentApiHeaders(data),
        })
      )
      .then((resp) => {
        const paymentIntent: PaymentIntent = resp.payment_intent;
        const attempt: PaymentAttempt = paymentIntent.active_payment_attempt;
        if (![PaymentAttemptStatus.REFUSED, PaymentAttemptStatus.AUTHORIZED].includes(attempt.status)) {
          this.setupPaymentIntentWorker(data);
        }
        return resp;
      })
      .catch((error) => {
        // this is to fix GATEENGG-21778
        Logger.kvl({
          action: 'failed_confirm_call',
          error_message: error.message,
          document_visiblility: document.hidden ? 'hidden' : 'visible',
          payment_method_type: data.payload.paymentMethodType,
          payment_intent_id: data.paymentIntentId,
        });
        if (data.payload.paymentMethodType == PaymentMethodType.UPI && error.message == 'Network Error') {
          this.setupPaymentIntentWorker(data);
          return {
            payment_intent: {
              ...paymentIntent,
              active_payment_attempt: {
                status: PaymentAttemptStatus.PENDING_CONFIRMATION,
              },
            },
          };
        } else {
          throw error;
        }
      });
  }

  cancelPaymentIntent(data: CancelPaymentIntentRequestPayload): Promise<ResponseInnerMessage> {
    const payload: any = {};
    if (data.reason) payload.reason = data.reason;
    return this.apiClient.payment_intent.cancel({paymentIntentId: data.paymentIntentId}, payload, {
      headers: getPaymentIntentApiHeaders(data),
    });
  }

  fetchGatewayCredential(data: any): Promise<ResponseInnerMessage> {
    return this.apiClient.payment_intent.fetch_gateway_credential(
      {paymentIntentId: data.paymentIntentId},
      {origin: data.origin},
      {headers: getPaymentIntentApiHeaders(data)}
    );
  }

  fetchGatewayToken(data: any): Promise<ResponseInnerMessage> {
    return this.apiClient.payment_intent.fetch_gateway_token({
      cbToken: data.cbToken,
    });
  }

  generateBraintreeClientToken(data: any): Promise<ResponseInnerMessage> {
    return this.apiClient.payment_intent.generate_braintree_client_token(
      {paymentIntentId: data.paymentIntentId},
      {},
      {headers: getPaymentIntentApiHeaders(data)}
    );
  }

  createBluesnapPfToken(data: any): Promise<ResponseInnerMessage> {
    return this.apiClient.payment_intent.create_bluesnap_pf_token(
      {paymentIntentId: data.paymentIntentId},
      data.payload,
      {headers: getPaymentIntentApiHeaders(data)}
    );
  }

  generateDeviceDataJWT(data: any): Promise<ResponseInnerMessage> {
    let param: any = {};
    if (data.cbToken) {
      param.cb_token = data.cbToken;
    }
    return this.apiClient.payment_intent.generate_device_data_jwt({paymentIntentId: data.paymentIntentId}, param, {
      headers: getPaymentIntentApiHeaders(data),
    });
  }

  setAdyen3DS1VerificationResult(data: any): Promise<ResponseInnerMessage> {
    // TODO Set payment attempt ID, since payment intent id is same for all payment attempts
    if (!this.store) this.store = {};
    this.store[data.paymentIntentId] = data;
    return Promise.resolve({acknowledged: true});
  }

  setCheckoutCom3DSVerificationResult(data: any): Promise<ResponseInnerMessage> {
    if (!this.store) this.store = {};
    this.store[data.paymentIntentId] = data;
    return Promise.resolve({acknowledged: true});
  }

  setCybersource3DSVerificationResult(data: any): Promise<ResponseInnerMessage> {
    if (!this.store) this.store = {};
    this.store[data.paymentIntentId] = data;
    return Promise.resolve({acknowledged: true});
  }

  pollAdyen3DS1VerificationResult(data: any): Promise<ResponseInnerMessage> {
    return new Promise((resolve, reject) => {
      const paymentIntentId = data.paymentIntentId;
      let counter = 0;
      const interval = setInterval(() => {
        // TODO throw proper error
        if (counter > ThreeDSPollingInterval.DEFAULT) {
          return reject({message: 'Poll Timeout'});
        }
        if (this.store && this.store[paymentIntentId]) {
          const _data: any = this.store[paymentIntentId];
          clearInterval(interval);

          // delete meta info passed
          delete _data.paymentIntentId;
          delete _data.src;

          // Reset store
          this.store[paymentIntentId] = null;

          return resolve(_data);
        }
        counter++;
      }, 100);
    });
  }

  pollCheckoutCom3DSVerificationResult(data: any): Promise<ResponseInnerMessage> {
    return new Promise((resolve, reject) => {
      const paymentIntentId = data.paymentIntentId;
      let counter = 0;
      const interval = setInterval(() => {
        // TODO throw proper error
        if (counter > ThreeDSPollingInterval.DEFAULT) {
          return reject({message: 'Poll Timeout'});
        }
        if (this.store && this.store[paymentIntentId]) {
          const _data: any = this.store[paymentIntentId];
          clearInterval(interval);

          // delete meta info passed
          delete _data.paymentIntentId;
          delete _data.src;

          // Reset store
          this.store[paymentIntentId] = null;

          return resolve(_data);
        }
        counter++;
      }, 100);
    });
  }

  pollCybersource3DSVerificationResult(data: any): Promise<ResponseInnerMessage> {
    return new Promise((resolve, reject) => {
      const paymentIntentId = data.paymentIntentId;
      let counter = 0;
      const interval = setInterval(() => {
        // TODO throw proper error
        if (counter > ThreeDSPollingInterval.DEFAULT) {
          return reject({message: 'Poll Timeout'});
        }
        if (this.store && this.store[paymentIntentId]) {
          const _data: any = this.store[paymentIntentId];
          clearInterval(interval);

          // delete meta info passed
          delete _data.paymentIntentId;
          delete _data.src;

          // Reset store
          this.store[paymentIntentId] = null;

          return resolve(_data);
        }
        counter++;
      }, 100);
    });
  }

  generateAdyenOriginKey(data: any): Promise<ResponseInnerMessage> {
    const payload = {
      origins: data.origin,
    };
    return this.apiClient.payment_intent.generate_adyen_origin_key({paymentIntentId: data.paymentIntentId}, payload, {
      headers: getPaymentIntentApiHeaders(data),
    });
  }

  constructor(commMgr: CommManagerInterface) {
    this.commMgr = commMgr;
    this.apiClient = commMgr.apiClient;
    this.iframeClient = commMgr.connectionClient;
  }

  setPaymentIntent3DSResult(data: any): Promise<ResponseInnerMessage> {
    if (!this.store) this.store = {};
    // App Side Confirm Call
    if (data.payment_intent) {
      // https://mychargebee.atlassian.net/browse/GATEENGG-19343
      // After 3ds verification, we can get attempt status as pending_authorization so
      // we will ignore this status and continue polling.
      if (
        data.payment_intent.active_payment_attempt &&
        data.payment_intent.active_payment_attempt.status === PaymentAttemptStatus.PENDING_AUTHORIZATION
      ) {
        return Promise.resolve({acknowledged: true});
      }
      this.store[data.payment_intent.id] = data;
      this.store['version_2'] = true;
    } else {
      // Client Side Confirm Call
      this.store[data.paymentIntentId] = data;
    }
    return Promise.resolve({acknowledged: true});
  }

  stopPollPaymentIntent3DSResult(data: any): Promise<ResponseInnerMessage> {
    if (!this.store) this.store = {};
    this.store[data.paymentIntentId] = data;
    this.store['version_2'] = true;
    return Promise.resolve({acknowledged: true});
  }

  pollPaymentIntent3DSResult(data: any): Promise<ResponseInnerMessage> {
    return new Promise((resolve, reject) => {
      const paymentIntentId = data.paymentIntentId;
      let counter = 0;
      const intervalPeriod = 100;
      const targetCounter = 600000 / intervalPeriod;
      const retrievePiInterval = 5000 / intervalPeriod; // 5s once
      this.piResolver = resolve;
      this.piRejector = reject;

      const interval = setInterval(() => {
        if (counter > targetCounter) {
          return reject({message: 'Poll Timeout'});
        }

        if (this.store && this.store[paymentIntentId] && this.store['version_2']) {
          // New Flow

          const _data: any = this.store[paymentIntentId];
          _data['version_2'] = true;
          clearInterval(interval);
          delete this.store[paymentIntentId];

          if (_data.error) {
            return reject(_data);
          }

          return resolve(_data);
        } else if (this.store && this.store[paymentIntentId]) {
          // Existing Flow

          const _data: any = this.store[paymentIntentId];
          clearInterval(interval);

          // delete meta info passed
          delete _data.paymentIntentId;
          delete _data.src;
          delete this.store[paymentIntentId];

          if (_data.error) {
            return reject(_data);
          }

          return resolve(_data);
        }
        counter++;
      }, intervalPeriod);
    });
  }

  retrieveBraintreePaymentMethodNonce(data: {nonce: string; paymentIntentId: string}): Promise<ResponseInnerMessage> {
    return this.apiClient.payment_intent.retrieve_braintree_payment_method_nonce(
      {nonce: data.nonce},
      {},
      {
        headers: {
          ...getPaymentIntentApiHeaders(data),
          'X-Requested-With': 'XMLHttpRequest',
        },
      }
    );
  }
}
